/**
 * Recovery escrow 크립토 (PR1 신모델 — escrow = encrypt(recovery_priv, wrkp_pub)).
 *
 * forge-only leaf 모듈: pUtils 를 import 하지 않는다. pUtils 의 register/changePW 가
 * 본 모듈을 import 하므로, 역방향 import 는 순환 참조를 만든다 (design §4.6).
 *
 * 포맷은 pUtils.hybridEncrypt(:1299)/decryptFromBase64(:381) 와 동일하게 맞춘다
 * (version=1, AES-256-GCM 12B IV + 16B tag, RSA-OAEP SHA-256/MGF1-SHA-256).
 * 동일 포맷이라 pUtils 측 복호(admin recovery 등)와 상호 호환된다.
 */
import forge from 'node-forge'

const HYBRID_VERSION = 1
const HYBRID_AES_KEY_BYTES = 32
const HYBRID_IV_BYTES = 12
const HYBRID_TAG_BYTES = 16

// forge md 객체는 stateful 이라 호출마다 새로 생성 (label digest 오염 방지).
// 빈 label SHA-256 이라 pUtils 의 모듈 레벨 oaepOpts(:376) 와 결과 동일.
const oaepOpts = () => ({
  md: forge.md.sha256.create(),
  mgf1: forge.mgf1.create(forge.md.sha256.create()),
})

// recovery_priv(PEM) → escrow 봉투 (WRKP 공개키로 hybrid 암호화). register/changePW write.
export const buildEscrow = (recoveryPrivPem, wrkpPublicKeyPem) => {
  const aesKey = forge.random.getBytesSync(HYBRID_AES_KEY_BYTES)
  const iv = forge.random.getBytesSync(HYBRID_IV_BYTES)

  const cipher = forge.cipher.createCipher('AES-GCM', aesKey)
  cipher.start({ iv, tagLength: HYBRID_TAG_BYTES * 8 })
  cipher.update(forge.util.createBuffer(recoveryPrivPem, 'utf8'))
  cipher.finish()
  const ciphertext = cipher.output.getBytes()
  const tag = cipher.mode.tag.getBytes()

  const publicKey = forge.pki.publicKeyFromPem(wrkpPublicKeyPem)
  const encryptedAesKey = publicKey.encrypt(aesKey, 'RSA-OAEP', oaepOpts())

  return {
    version: HYBRID_VERSION,
    encrypted_aes_key: forge.util.encode64(encryptedAesKey),
    encrypted_data: forge.util.encode64(iv + ciphertext + tag),
  }
}

// escrow 봉투 → recovery_priv(PEM) (WRKP 개인키로 복호). PR2 admin recovery read.
export const openEscrow = (payload, wrkpPrivateKeyPem) => {
  if (payload.version !== HYBRID_VERSION) {
    throw new Error(`Unsupported escrow version: ${payload.version}`)
  }
  const privateKey = forge.pki.privateKeyFromPem(wrkpPrivateKeyPem)
  const aesKey = privateKey.decrypt(
    forge.util.decode64(payload.encrypted_aes_key),
    'RSA-OAEP',
    oaepOpts(),
  )

  const raw = forge.util.decode64(payload.encrypted_data)
  const iv = raw.substring(0, HYBRID_IV_BYTES)
  const ciphertext = raw.substring(HYBRID_IV_BYTES, raw.length - HYBRID_TAG_BYTES)
  const tag = raw.substring(raw.length - HYBRID_TAG_BYTES)

  const decipher = forge.cipher.createDecipher('AES-GCM', aesKey)
  decipher.start({ iv, tagLength: HYBRID_TAG_BYTES * 8, tag: forge.util.createBuffer(tag) })
  decipher.update(forge.util.createBuffer(ciphertext))
  if (!decipher.finish()) throw new Error('openEscrow: integrity check failed')
  return decipher.output.toString('utf8')
}

// UMK → enc_umk (recovery 공개키로 RSA-OAEP 암호화 후 base64). enrollment/changePW write.
export const buildEncUmk = (umk, recoveryPublicKeyPem) => {
  const publicKey = forge.pki.publicKeyFromPem(recoveryPublicKeyPem)
  const encrypted = publicKey.encrypt(umk, 'RSA-OAEP', oaepOpts())
  return forge.util.encode64(encrypted)
}

// enc_umk → UMK (recovery 개인키로 복호). PR2/PR3 recovery read.
export const openEncUmk = (encUmkBase64, recoveryPrivateKeyPem) => {
  const privateKey = forge.pki.privateKeyFromPem(recoveryPrivateKeyPem)
  return privateKey.decrypt(forge.util.decode64(encUmkBase64), 'RSA-OAEP', oaepOpts())
}

// recovery_priv 를 wrkp_pub 보유 전체 ws 의 escrow 봉투로 생성 (PR2 Settings→SERVER 전환 공통 헬퍼).
// wrkpList = GET /v1/users/me/wrkp-public-keys 의 wrkp_public_keys[]: {workspace_id, user_workspace_id, wrkp_public_key}.
// wrkp_public_key 가 없는(WRKP 미초기화) ws 는 제외 — 백엔드 bulk_save 도 skip 하나, 명시 필터로 payload 축소 + D-GATE 정합.
// 새 recovery keypair 회전 시 전역 enc_umk 와 정합을 위해 *전체 ws* 를 재escrow 해야 함(design §4 F2).
// 반환 entry = 백엔드 EscrowedRecoveryKeyEntry 스키마: {workspace_id, user_workspace_id, encrypted_recovery_key}.
export const buildEscrowedKeysForWorkspaces = (recoveryPrivPem, wrkpList) =>
  (wrkpList || [])
    .filter((ws) => ws && ws.wrkp_public_key)
    .map((ws) => ({
      workspace_id: ws.workspace_id,
      user_workspace_id: ws.user_workspace_id,
      encrypted_recovery_key: buildEscrow(recoveryPrivPem, ws.wrkp_public_key),
    }))

// 균등분포 보안 난수 정수 [0, max) — crypto.getRandomValues + rejection sampling(modulo 편향 제거).
// Math.random 금지(비암호학적). PR3 admin recovery temp_password 생성용.
const secureRandomInt = (max) => {
  const arr = new Uint32Array(1)
  const limit = Math.floor(0xffffffff / max) * max
  let x = limit
  while (x >= limit) {
    crypto.getRandomValues(arr)
    ;[x] = arr
  }
  return x % max
}

// admin ESCROW 복구 시 대상 사용자의 임시 비밀번호 생성 (= 대상의 new UMK, 평문).
// 백엔드 verify-otp 번들 temp_password_policy 준수:
//   { length, allowed_chars, special_chars, min_special }
// min_special 개의 특수문자 보장 + 나머지는 allowed_chars에서 추출 후 Fisher-Yates 셔플(보안 난수).
export const generateTempPassword = (policy) => {
  const {
    length, allowed_chars: allowedChars, special_chars: specialChars, min_special: minSpecial,
  } = policy || {}
  if (!length || !allowedChars) {
    throw new Error('generateTempPassword: invalid policy (length/allowed_chars required)')
  }
  const needSpecial = minSpecial || 0
  if (needSpecial > 0 && !specialChars) {
    throw new Error('generateTempPassword: min_special>0 but special_chars empty')
  }
  if (needSpecial > length) {
    throw new Error('generateTempPassword: min_special exceeds length')
  }

  const chars = []
  for (let i = 0; i < needSpecial; i += 1) {
    chars.push(specialChars[secureRandomInt(specialChars.length)])
  }
  for (let i = chars.length; i < length; i += 1) {
    chars.push(allowedChars[secureRandomInt(allowedChars.length)])
  }
  // Fisher-Yates 셔플 (특수문자 위치 무작위화)
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = secureRandomInt(i + 1)
    const tmp = chars[i]
    chars[i] = chars[j]
    chars[j] = tmp
  }
  return chars.join('')
}
